"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { CARD, TABLE, TD, TH, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { inputLines } from "@/lib/tool-kit/identifiers/identifier-input";
import type { CheckDigitWorking } from "@/lib/tool-kit/identifiers/isbn";
import {
  analyzeIssn,
  findIssnTokens,
  type IssnAnalysis,
} from "@/lib/tool-kit/identifiers/issn";

/** English strings. A translated content file overrides these through `ui`. */
export const ISSN_VALIDATOR_STRINGS = {
  inputLabel: "ISSNs — one per line, or paste a journal's details",
  inputPlaceholder:
    "0378-5955\neISSN: 1476-4687\nISSN 0028-0836 (print), 1474-547X (online)",
  inputHint:
    "Every ISSN-shaped number in a line is checked, so a masthead or a reference list works too. Nothing is sent anywhere.",
  emptyPrompt: "Paste an ISSN to check its format and check digit.",
  results: "Results",
  validCount: "Valid",
  invalidCount: "Not valid",
  valid: "Valid ISSN",
  invalid: "Not a valid ISSN",
  badLength:
    "An ISSN is eight characters — seven digits and a check digit — written NNNN-NNNC. This one has {count}.",
  badCharacters: "Only digits belong in an ISSN, plus X as the last character.",
  badX: "X can only be the check character — the last of the eight.",
  badCheck:
    "The check digit should be {expected}, not {actual}. One of the other digits is probably mistyped.",
  validNote:
    "A valid check digit proves an ISSN is well formed, not that it was assigned — look it up in the ISSN Portal to see which serial it belongs to.",
  checkMatches: "The check digit, {check}, matches the other seven digits.",
  showWorking: "Show the check-digit working",
  digitRow: "Digit",
  weightRow: "Weight",
  productRow: "Product",
  workingSum: "Sum of the products: {sum}",
  workingRemainder: "{sum} mod 11 = {remainder}",
  workingCheck: "Check digit: (11 − {remainder}) mod 11 = {value}",
  workingX: "A check value of 10 is written X.",
  copyValid: "Copy valid ISSNs",
  copied: "Copied!",
} as const;

type Strings = typeof ISSN_VALIDATOR_STRINGS;

const MAX_ITEMS = 1000;

interface Item {
  key: string;
  analysis: IssnAnalysis;
}

/** Every ISSN-shaped token; a line with none is checked whole, so its problem shows. */
function itemsFrom(text: string): Item[] {
  const items: Item[] = [];
  for (const [lineIndex, line] of inputLines(text).entries()) {
    const tokens = findIssnTokens(line);
    const candidates = tokens.length > 0 ? tokens : [line];
    for (const [tokenIndex, token] of candidates.entries()) {
      items.push({
        key: `${lineIndex}-${tokenIndex}`,
        analysis: analyzeIssn(token),
      });
    }
  }
  return items.slice(0, MAX_ITEMS);
}

function problemText(analysis: IssnAnalysis, strings: Strings): string {
  switch (analysis.problem) {
    case "length":
      return t(strings.badLength, { count: analysis.compact.length });
    case "x-position":
      return strings.badX;
    case "check-digit":
      return t(strings.badCheck, {
        expected: analysis.expectedCheck ?? "",
        actual: analysis.compact.at(-1) ?? "",
      });
    case undefined:
      return t(strings.checkMatches, { check: analysis.compact.at(-1) ?? "" });
    default:
      return strings.badCharacters;
  }
}

function Working({
  working,
  strings,
}: {
  working: CheckDigitWorking;
  strings: Strings;
}) {
  const rows: [string, number[]][] = [
    [strings.digitRow, working.digits],
    [strings.weightRow, working.weights],
    [strings.productRow, working.products],
  ];
  return (
    <div className="mt-3 space-y-2">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className={TABLE}>
          <tbody>
            {rows.map(([label, values]) => (
              <tr key={label}>
                <th className={TH} scope="row">
                  {label}
                </th>
                {values.map((value, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                  <td className={`${TD} text-center font-mono`} key={index}>
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-foreground text-sm">
        {t(strings.workingSum, { sum: working.sum })}
        <br />
        {t(strings.workingRemainder, {
          sum: working.sum,
          remainder: working.remainder,
        })}
        <br />
        {t(strings.workingCheck, {
          remainder: working.remainder,
          value: (11 - working.remainder) % 11,
        })}
      </p>
      {working.check === "X" ? (
        <p className="text-muted-foreground text-sm">{strings.workingX}</p>
      ) : null}
    </div>
  );
}

export default function IssnValidatorWidget() {
  const strings = useToolUi(ISSN_VALIDATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");

  const items = itemsFrom(text);
  const valid = items.filter((item) => item.analysis.valid);
  const validList = [
    ...new Set(valid.map((item) => item.analysis.formatted)),
  ].join("\n");

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="issnv-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={5}
        value={text}
      />
      <div aria-live="polite" className="space-y-4">
        {items.length === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.validCount}
                value={fmt(valid.length)}
              />
              <StatCard
                label={strings.invalidCount}
                value={fmt(items.length - valid.length)}
              />
            </StatGrid>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyValid}
              text={validList}
            />
            {valid.length > 0 ? <Notice>{strings.validNote}</Notice> : null}
            <ul className="space-y-3">
              {items.map(({ key, analysis }) => (
                <li className={`${CARD} space-y-2`} key={key}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="break-all font-mono text-foreground">
                      {analysis.compact.length === 8
                        ? analysis.formatted
                        : analysis.input}
                    </span>
                    <span
                      className={
                        analysis.valid
                          ? "font-medium text-emerald-700 text-sm dark:text-emerald-400"
                          : "font-medium text-destructive text-sm"
                      }
                    >
                      {analysis.valid ? strings.valid : strings.invalid}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {problemText(analysis, strings)}
                  </p>
                  {analysis.working ? (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-primary">
                        {strings.showWorking}
                      </summary>
                      <Working strings={strings} working={analysis.working} />
                    </details>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
